/**
 * "App version" section in Settings.
 *
 * Shares useUpdate() with the banner, so permission handling and the
 * resume-after-Settings behaviour are identical in both places rather than
 * implemented twice and drifting apart.
 *
 * The automatic banner only appears when there IS an update, which means a
 * broken check looks exactly like being current — that is how both a CORS
 * failure and a hung native call hid here. This makes the check explicit and
 * reports what actually happened, including which step failed.
 */

import { isNative } from '../native/platform'
import { useUpdate } from '../native/useUpdate'

export function UpdateCheck() {
  const {
    status, phase, percent, error, checkedAt, check, update, openSettings,
    isSnoozed, snoozedUntil, unsnooze,
  } = useUpdate()

  // On web the browser always has the current build; a row saying so is noise.
  if (!isNative()) return null

  const latest = status?.latest
  const busy = phase === 'downloading'
  const waiting = phase === 'awaiting-permission'
  const installing = phase === 'installing'

  return (
    <>
      <h2>App version</h2>
      <div className="card">
        {error && <div className="error">{error}</div>}

        <ul className="plain" style={{ margin: 0 }}>
          <li>
            <span>Installed</span>
            <span className="muted">{status?.current ?? '—'}</span>
          </li>
          <li>
            <span>Latest published</span>
            <span className="muted">
              {latest?.versionName
                ?? (phase === 'up-to-date' ? 'same as installed' : '—')}
            </span>
          </li>
          {checkedAt && (
            <li>
              <span>Last checked</span>
              <span className="muted">
                {checkedAt.toLocaleTimeString([], {
                  hour: 'numeric', minute: '2-digit' })}
              </span>
            </li>
          )}
        </ul>

        {status?.available && latest?.notes && (
          <div
            style={{
              margin: '.6rem 0 0',
              padding: '.55rem .7rem',
              background: 'var(--surface-2)',
              borderRadius: 'var(--radius)',
              fontSize: '.85rem',
              whiteSpace: 'pre-line',
            }}
          >
            {latest.notes}
          </div>
        )}

        {/* A hidden banner should never be a mystery. If you tapped Later and
            then wondered where it went, this says so and puts it back. */}
        {isSnoozed && snoozedUntil && (
          <div className="row" style={{ marginTop: '.6rem' }}>
            <span className="muted" style={{ fontSize: '.82rem' }}>
              Banner hidden until{' '}
              {new Date(snoozedUntil).toLocaleTimeString([], {
                hour: 'numeric', minute: '2-digit' })}
            </span>
            <button
              className="secondary"
              style={{ marginTop: 0, padding: '.25rem .6rem', minHeight: 0,
                       fontSize: '.8rem' }}
              onClick={unsnooze}
            >
              Show it
            </button>
          </div>
        )}

        {status?.available && latest ? (
          busy ? (
            <p className="muted" style={{ marginBottom: 0 }}>
              {percent === null ? 'Starting…' : `Downloading… ${percent}%`}
            </p>
          ) : waiting ? (
            <>
              <p className="muted" style={{ marginBottom: '.5rem' }}>
                Android needs permission to let Lift install apps. Turn it on,
                then come back — the download starts by itself.
              </p>
              <button style={{ width: '100%' }} onClick={() => void openSettings()}>
                Open Settings
              </button>
            </>
          ) : installing ? (
            <>
              <p className="muted" style={{ marginBottom: '.5rem' }}>
                Downloaded and waiting on Android's installer. If you dismissed
                it, tap below to try again.
              </p>
              <button style={{ width: '100%' }} onClick={() => void update()}>
                Install {latest.versionName}
              </button>
            </>
          ) : (
            <button style={{ width: '100%' }} onClick={() => void update()}>
              Update to {latest.versionName}
            </button>
          )
        ) : (
          <button
            className="secondary"
            style={{ width: '100%' }}
            disabled={phase === 'checking'}
            onClick={() => void check()}
          >
            {phase === 'checking' ? 'Checking…' : 'Check for updates'}
          </button>
        )}
      </div>
    </>
  )
}
